import { t } from "../../i18n";
import type { Permission } from "@lumen/shared";
import { KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { ConfirmDangerDialog } from "../../components/shared/ConfirmDangerDialog";
import { SettingsPage } from "../settings/ProfilePage";
import { PersonPermissions } from "../../components/organization/PersonPermissions";

export type PermissionEffect = "allow" | "deny";
type PermissionOverride = {
  permissionId: string;
  permissionKey?: Permission;
  effect: PermissionEffect;
};
type Role = { id: string; name: string; description: string };
export type Person = {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  disabled: boolean;
  roles: Role[];
  inheritedPermissionIds: string[];
  effectivePermissionIds: string[];
  permissionOverrides: PermissionOverride[];
};
export type PermissionRow = {
  id?: string;
  key: Permission;
  description: string;
};
const fallbackPermissions: PermissionRow[] = [
  { key: "site:providers:read", description: "View platform providers" },
  { key: "site:providers:write", description: "Manage platform providers" },
  { key: "site:models:read", description: "View platform models" },
  { key: "site:models:write", description: "Manage platform models" },
  { key: "site:tools:read", description: "View platform tools" },
  { key: "site:tools:write", description: "Manage platform tools" },
  { key: "site:mcp:manage", description: "Manage platform MCP connections" },
  { key: "site:logs:read", description: "View platform logs" },
  { key: "site:organizations:read", description: "View every organization" },
  { key: "site:organizations:manage", description: "Manage organizations" },
  { key: "site:users:read", description: "View site-wide users" },
  { key: "site:users:manage", description: "Manage site-wide users" },
  {
    key: "site:organization-users:manage",
    description: "Manage organization users",
  },
  {
    key: "site:organization-access:manage",
    description: "Assign platform resources",
  },
  {
    key: "site:organization-billing:manage",
    description: "Manage organization billing",
  },
  {
    key: "site:organization-auth:manage",
    description: "Manage organization authentication",
  },
  { key: "site:pricing:manage", description: "Manage plans and prices" },
  { key: "site:agents:manage", description: "Manage platform agents" },
  { key: "site:skills:manage", description: "Manage platform skills" },
  { key: "site:usage:read", description: "View cross-organization usage" },
  { key: "site:settings:manage", description: "Manage site-wide settings" },
];

export function UsersPage({
  canWrite,
  currentUserId,
}: {
  canWrite: boolean;
  currentUserId: string;
}) {
  const [people, setPeople] = useState<Person[]>([]);
  const [permissions, setPermissions] =
    useState<PermissionRow[]>(fallbackPermissions);
  const [saving, setSaving] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Person | null>(null);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const load = async () => {
    setError("");
    try {
      const [nextPeople, nextPermissions] = await Promise.all([
        api<Person[]>("/admin/users"),
        api<PermissionRow[]>("/admin/permissions"),
      ]);
      setPeople(
        nextPeople.map((person) => ({
          ...person,
          roles: person.roles ?? [],
          inheritedPermissionIds: person.inheritedPermissionIds ?? [],
          effectivePermissionIds: person.effectivePermissionIds ?? [],
          permissionOverrides: person.permissionOverrides ?? [],
        })),
      );
      setPermissions(nextPermissions);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Access settings could not be loaded",
      );
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const setPermission = (
    userId: string,
    permissionId: string,
    effect: PermissionEffect | "inherit",
  ) => {
    setSaved("");
    setPeople((current) =>
      current.map((person) => {
        if (person.id !== userId) return person;
        const permissionOverrides =
          effect === "inherit"
            ? person.permissionOverrides.filter(
                (item) => item.permissionId !== permissionId,
              )
            : [
                ...person.permissionOverrides.filter(
                  (item) => item.permissionId !== permissionId,
                ),
                { permissionId, effect },
              ];
        const effectivePermissionIds = new Set(person.inheritedPermissionIds);
        for (const override of permissionOverrides) {
          if (override.effect === "allow")
            effectivePermissionIds.add(override.permissionId);
          else effectivePermissionIds.delete(override.permissionId);
        }
        return {
          ...person,
          permissionOverrides,
          effectivePermissionIds: [...effectivePermissionIds],
        };
      }),
    );
  };
  const savePermissions = async (person: Person) => {
    setSaving(person.id);
    setSaved("");
    setError("");
    try {
      await api(`/admin/users/${person.id}/permission-overrides`, {
        method: "PUT",
        body: JSON.stringify({
          overrides: person.permissionOverrides.map(
            ({ permissionId, effect }) => ({ permissionId, effect }),
          ),
        }),
      });
      setSaved(person.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Access could not be saved");
    } finally {
      setSaving("");
    }
  };
  const deleteUser = async () => {
    if (!deleteTarget) return;
    const person = deleteTarget;
    setDeleting(person.id);
    setError("");
    try {
      await api(`/admin/users/${person.id}`, { method: "DELETE" });
      setPeople((current) => current.filter((item) => item.id !== person.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "User could not be deleted");
    } finally {
      setDeleting("");
    }
  };
  return (
    <SettingsPage
      title={t("People & permissions")}
      description="See each person’s roles, inherited permissions, individual overrides, and effective access."
    >
      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      <div className="mb-3 flex items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950/30 dark:text-violet-300">
          <KeyRound size={17} />
        </div>
        <div>
          <h3 className="font-bold">{t("Individual access overrides")}</h3>
          <p className="mt-1 text-sm text-stone-500">
            {t(
              "Allow or deny individual capabilities for each person while leaving all other access inherited.",
            )}
          </p>
        </div>
      </div>
      <div className="panel overflow-hidden">
        {people.map((person) => (
          <PersonPermissions
            key={person.id}
            person={person}
            permissions={permissions}
            canWrite={canWrite}
            isCurrentUser={person.id === currentUserId}
            saving={saving === person.id}
            deleting={deleting === person.id}
            saved={saved === person.id}
            onChange={(permissionId, checked) =>
              setPermission(person.id, permissionId, checked)
            }
            onSave={() => void savePermissions(person)}
            onDelete={() => setDeleteTarget(person)}
          />
        ))}
      </div>
      {people.length === 0 && (
        <div className="py-14 text-center text-sm text-stone-400">
          {t("No users have signed in yet.")}
        </div>
      )}
      {deleteTarget && (
        <ConfirmDangerDialog
          title={`Delete ${deleteTarget.name}?`}
          description={`This permanently deletes ${deleteTarget.email}, including their conversations, settings, and other owned data. This action cannot be undone.`}
          confirmLabel="Delete user"
          busy={deleting === deleteTarget.id}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void deleteUser()}
        />
      )}
    </SettingsPage>
  );
}
